import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * v29.1 — lazy-loads via IntersectionObserver, only loads sources when in viewport,
 * pauses when out of view. Skipped entirely on mobile (CSS display:none).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    if (!videoElement) return;

    // Skip on mobile (matches CSS @media (max-width: 749px) { video { display: none } }).
    // Save bandwidth: don't even register the observer.
    if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
      return;
    }

    // Respect reduced-motion preference.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#ensureLoaded();
            const playPromise = videoElement.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          } else if (!videoElement.paused) {
            videoElement.pause();
          }
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    this.#observer.observe(this);

    // Fade out poster once the video starts playing.
    videoElement.addEventListener(
      'playing',
      () => {
        this.setAttribute('data-video-ready', 'true');
      },
      { once: true }
    );
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  #ensureLoaded() {
    if (this.#loaded) return;
    const { videoSources, videoElement } = this.refs;
    if (!videoSources || !videoElement) return;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource && !source.getAttribute('src')) {
        source.setAttribute('src', videoSource);
      }
    }
    videoElement.load();
    this.#loaded = true;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
